/**
 * ============================================================================
 * Iron Meridian - Import/Export Controls Component
 * ============================================================================
 * 
 * Handles exporting saves to JSON files and importing from JSON files.
 * Validates import data and provides user feedback.
 */

import { useState, useRef } from 'react'
import * as saveService from '../services/saveService.js'

// ============================================================================
// IMPORT PREVIEW
// ============================================================================

function ImportPreview({ data, onConfirm, onCancel, isImporting }) {
  if (!data) return null

  const { save, replayTurns } = data

  return (
    <div className="bg-war-room-700/50 border border-war-room-600/50 rounded-lg p-4">
      <h3 className="text-white font-medium mb-3">Import Preview</h3>
      
      <div className="space-y-3 text-sm">
        <div className="grid grid-cols-2 gap-x-4 gap-y-2">
          <div className="text-gray-400">Save Name</div>
          <div className="text-white">{save?.displayName}</div>
          
          <div className="text-gray-400">Turn</div>
          <div className="text-white">{save?.turnNumber}</div>
          
          <div className="text-gray-400">Country</div>
          <div className="text-white">{save?.playerCountry}</div>
          
          <div className="text-gray-400">Replay Turns</div>
          <div className="text-white">{replayTurns?.length || 0}</div>
          
          <div className="text-gray-400">Exported</div>
          <div className="text-white">
            {data.exportedAt ? new Date(data.exportedAt).toLocaleString() : 'Unknown'}
          </div>
        </div>

        <div className="text-xs text-gray-500">
          Save will be created as: <span className="text-gray-300">{save?.playerCountry} - Turn {save?.turnNumber}</span>
        </div>

        <div className="flex gap-2 pt-2">
          <button
            onClick={() => onConfirm()}
            disabled={isImporting}
            className="flex-1 px-4 py-2 bg-green-500/20 hover:bg-green-500/30 
                       text-green-400 font-medium rounded transition-colors
                       disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isImporting ? 'Importing...' : 'Import Save'}
          </button>
          <button
            onClick={onCancel}
            disabled={isImporting}
            className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function ImportExportControls({ 
  onImportComplete,
  className = ''
}) {
  const [importData, setImportData] = useState(null)
  const [isImporting, setIsImporting] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const fileInputRef = useRef(null)

  // Handle file selection
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    setError(null)
    setSuccess(null)

    try {
      const data = await saveService.readImportFile(file)
      const validation = saveService.validateImport(data)
      
      if (!validation.valid) {
        setError(`Invalid save file: ${validation.errors.join(', ')}`)
        return
      }

      setImportData(data)
    } catch (err) {
      setError(`Failed to read file: ${err.message}`)
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  // Handle import confirmation
  const handleConfirmImport = async () => {
    if (!importData) return

    setIsImporting(true)
    setError(null)

    try {
      const result = await saveService.importSave(importData)
      setSuccess(`Successfully imported "${result.displayName}" at turn ${result.turnNumber}`)
      setImportData(null)
      
      if (onImportComplete) {
        onImportComplete(result)
      }
    } catch (err) {
      setError(`Import failed: ${err.message}`)
    } finally {
      setIsImporting(false)
    }
  }

  // Cancel import
  const handleCancelImport = () => {
    setImportData(null)
    setError(null)
  }

  // Clear messages after timeout
  const clearSuccess = () => {
    setTimeout(() => setSuccess(null), 5000)
  }
  if (success) clearSuccess()

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Error message */}
      {error && (
        <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-sm flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300">✕</button>
        </div>
      )}

      {/* Success message */}
      {success && (
        <div className="p-3 bg-green-500/20 border border-green-500/30 rounded-lg text-green-400 text-sm flex justify-between items-center">
          <span>✓ {success}</span>
          <button onClick={() => setSuccess(null)} className="text-green-400 hover:text-green-300">✕</button>
        </div>
      )}

      {/* Import preview */}
      {importData && (
        <ImportPreview
          data={importData}
          onConfirm={handleConfirmImport}
          onCancel={handleCancelImport}
          isImporting={isImporting}
        />
      )}

      {/* Import button */}
      {!importData && (
        <div className="flex gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleFileSelect}
            className="hidden"
            id="import-file-input"
          />
          
          <label
            htmlFor="import-file-input"
            className="flex-1 px-4 py-3 border-2 border-dashed border-war-room-600/50 rounded-lg
                       text-gray-400 hover:text-white hover:border-green-500/50
                       transition-colors cursor-pointer flex items-center justify-center gap-2"
          >
            <span className="text-lg">⬆</span>
            Import Save File
          </label>
        </div>
      )}
    </div>
  )
}

export default ImportExportControls
